/* eslint-disable react/no-array-index-key */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable max-lines */
import { BarDatum, BarTooltipProps, ComputedDatum, ResponsiveBar } from '@nivo/bar';
import { clearTree, requestTreeFiltersToogle } from 'actions';
import { allowedReadOnlyDimensions, metricsLinksMap } from 'constants/dashboard';
import { FULL_DATE_FORMAT } from 'constants/formats';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import withWidget from 'decorators/widget';
import WidgetLoading from 'decorators/widget/WidgetLoading';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { getCustomerDomain, getUser } from 'selectors/auth';
import { getTree } from 'selectors/dashboard';
import { getNavParamsByResourceMemo, getNavWidgetFiltersMemo } from 'selectors/nav';
import { getPermissionsPolicy } from 'selectors/permissions';
import { useSelector } from 'store';
import { DefaultWidgetComponentProps } from 'types';
import {
  DateHistogramDataReturnType,
  buildDateHistogramDataFromMetrics,
  joinQueryFilters,
} from 'utils/dashboard';
import {
  getValuesFromDimensions,
  removeField,
  setNewValue,
  transformToString,
} from 'utils/parserTree';
import { isActionAuthorized } from 'utils/permissions';
import { useHistory } from 'utils/urls';
import composeLineChart from './ComposeLineChart';

type Coords = {
  left?: number;
  top?: number;
};

const ToolTip = ({
  datum,
  processedData,
  coords,
}: {
  datum: BarTooltipProps<BarDatum>;
  processedData: DateHistogramDataReturnType;
  coords: Coords | null;
}): JSX.Element => {
  const portal = document.getElementById('tooltip-portal');

  const ttip = (
    <div
      data-testid="tooltip-text"
      style={{
        left: (coords?.left || 0) + 10,
        top: coords?.top,
      }}
      className=" bg-litlingo-off-white p-1 rounded flex items-center shadow-md fixed"
    >
      <div
        style={{
          background: datum.color,
        }}
        className="min-h-3 min-w-3 h-3 w-3 mr-2"
      />
      <span className="text">
        {processedData.labelsMap[datum.id]} - {processedData.labelsMap[datum.indexValue]}:{' '}
        <b>{datum.value}</b>
      </span>
    </div>
  );

  if (portal) {
    return ReactDOM.createPortal(ttip, portal);
  }
  return <></>;
};

export const PendingReviewWidget: React.FC<DefaultWidgetComponentProps> = (props) => {
  const { widgetId, widgetData, queryData, nivoProps } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(getUser);
  const policy = useSelector(getPermissionsPolicy);
  const customerDomain = useSelector(getCustomerDomain);
  const widgetFilters = useSelector((state) => getNavWidgetFiltersMemo(state, widgetId));
  const filters = useSelector((state) =>
    getNavParamsByResourceMemo(state, resourceQueryParamName.metrics)
  );
  const urlTree = useSelector(getTree);

  const [coords, setCoords] = useState<Coords | null>({});

  if (widgetData == null || queryData == null) {
    return <WidgetLoading />;
  }

  const processedData = buildDateHistogramDataFromMetrics(
    // @ts-ignore
    { ...widgetData, results: [widgetData.results[0]] },
    queryData
  );

  const handleClick = (datum: ComputedDatum<BarDatum>): void => {
    const query = queryData.queries.find((q) => q.id === datum.id);
    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info == null || !isActionAuthorized(info.action, user.roles, policy)) {
      return;
    }

    const series = widgetData.results.find((s) => s.id === datum.id);
    if (series == null) {
      return;
    }

    const start = Number(series.x_axis[datum.index].value) * 1000;
    const end = Number(series.x_axis[datum.index + 1]?.value ?? queryData.end_time) * 1000;

    const infoDimension = info.dimensions;

    // TODO: extract this to a utility function
    const dimensions: Record<string, string[]> = {};
    if (infoDimension != null) {
      Object.entries(infoDimension).forEach(([dimension, param]) => {
        const allowed = allowedReadOnlyDimensions[query.metric];

        // if allowedDimension is not defined or the dimension is not included,
        // just ignore it
        if (allowed == null || !allowed.includes(dimension)) {
          return;
        }

        const value = [];
        if (filters[dimension] != null) {
          const topValue = filters[dimension];
          if (Array.isArray(topValue)) {
            topValue.forEach((v) => value.push(v));
          } else {
            value.push(topValue);
          }
        }

        if (widgetFilters[dimension] != null) {
          const widgetValue = filters[dimension];
          if (Array.isArray(widgetValue)) {
            widgetValue.forEach((v) => value.push(v));
          } else {
            value.push(widgetValue);
          }
        }

        dimensions[`${info.resource}__${param}`] = value;
      });

      if (query.dimensions != null) {
        query.dimensions.forEach((dimension) => {
          const allowed = allowedReadOnlyDimensions[query.metric];

          // if allowedDimension is not defined or the dimension is not included,
          // just ignore it
          if (allowed == null || !allowed.includes(dimension.name)) {
            return;
          }

          if (!dimensions[`${info.resource}__${infoDimension[dimension.name]}`]) {
            dimensions[`${info.resource}__${infoDimension[dimension.name]}`] = [dimension.value];
          } else if (
            !dimensions[`${info.resource}__${infoDimension[dimension.name]}`].includes(
              dimension.value
            )
          ) {
            dimensions[`${info.resource}__${infoDimension[dimension.name]}`] = [
              ...dimensions[`${info.resource}__${infoDimension[dimension.name]}`],
              dimension.value,
            ];
          }
        });
      }
    }

    if (query.metric.includes('pending_review')) {
      dimensions[`${info.resource}__review_values`] = ['pending'];
    }

    const startValue = moment.utc(start).format(FULL_DATE_FORMAT);
    const endValue = moment.utc(end).format(FULL_DATE_FORMAT);

    // @ts-ignore
    const widgetFilterSearch = query.filters_search || '';

    const tree = getValuesFromDimensions(dimensions);
    const newTree = setNewValue(
      cloneDeep(tree),
      'date_range',
      `${startValue}<>${endValue}`,
      '',
      true
    );

    let searchString = transformToString(newTree);
    if (typeof widgetFilterSearch === 'string') {
      searchString = joinQueryFilters(searchString, widgetFilterSearch);
    }
    const modifiedFilters = removeField(cloneDeep(urlTree), 'date_range');
    searchString = joinQueryFilters(searchString, transformToString(modifiedFilters));

    dispatch(
      requestTreeFiltersToogle({
        value: true,
      })
    );
    dispatch(clearTree());
    const hasEventsFilter = ['true'];

    history.pushLookup({
      customerDomain,
      routeName: info.route,
      queryParams: {
        [`${info.resource}__filters_search`]: searchString,
        [`${info.resource}__has_events`]: hasEventsFilter,
      },
    });
  };

  const handleMouseEnter = (
    datum: ComputedDatum<BarDatum>,
    event: React.MouseEvent<SVGRectElement, MouseEvent>
  ): void => {
    setCoords({
      left: event.pageX,
      top: event.pageY,
    });

    const query = queryData.queries.find((q) => q.id === datum.id);
    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info != null) {
      event.currentTarget.style.cursor = 'pointer';
    }
  };

  const data: number[] = widgetData.results[1].y_axis.map((val) => val);

  return (
    <div className="h-full w-full flex flex-row items-center gap-2 chart-svg-overflow">
      <div className="flex min-w-5 w-5">
        <div className="flex flex-row items-center gap-1 transform origin-top -translate-x-1/2 -rotate-90">
          <svg width={14} height={14} viewBox="0 0 14 14">
            <rect width={14} height={14} fill="#007656" />
          </svg>
          <span className="text-small whitespace-no-wrap">{widgetData.results[0].label}</span>
        </div>
      </div>
      <div className="h-full w-full">
        <ResponsiveBar
          data={processedData.data}
          keys={processedData.keys}
          indexBy={processedData.indexBy}
          margin={{ top: 10, right: 10, bottom: 50, left: 50 }}
          innerPadding={2}
          enableLabel={false}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          theme={{
            tooltip: {
              container: {
                background: 'none',
                boxShadow: 'none',
              },
            },
          }}
          axisBottom={{
            tickRotation: -49,
            legendOffset: 30,
            tickPadding: 5,
            format: (value) => processedData.labelsMap[value],
          }}
          axisLeft={{
            format: (value): string => (Math.floor(value) === value ? value : ''),
          }}
          animate={false}
          tooltip={(datum: BarTooltipProps<BarDatum>): JSX.Element => (
            <ToolTip datum={datum} processedData={processedData} coords={coords} />
          )}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          layers={['grid', 'axes', 'bars', composeLineChart(data), 'markers', 'legends']}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...nivoProps}
        />
      </div>
      <div className="flex w-5 ml-5 ">
        <div className="flex flex-row items-center gap-1 transform origin-top -translate-x-1/2 -rotate-90">
          <svg width={14} height={14} viewBox="0 0 14 14" fill="#F8A01A">
            <rect width={14} height={4} y={6} />
            <circle r={5} cx={7} cy={8} />
          </svg>
          <span className="text-small whitespace-no-wrap">{widgetData.results[1].label}</span>
        </div>
      </div>
    </div>
  );
};

export default withWidget({
  title: 'Models',
  reRenderWhitGrouping: true,
  allowEditWidget: true,
})(PendingReviewWidget);
